import { Component } from '@theme/component';
import { getProductReviews, formatReviewScore, reviewLabel } from '@theme/levayo-review-data';

class LevayoRating extends Component {
  static observedAttributes = ['data-product-id'];
  #controller;
  #observer;
  #request = 0;

  attributeChangedCallback(name, previous, next) {
    if (name !== 'data-product-id' || previous === next || !this.#controller || !this.isConnected) return;
    this.hidden = true;
    this.#load();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const section = Array.from(document.querySelectorAll('levayo-product-reviews[data-product-id]')).find(element => element.dataset.productId === this.dataset.productId);
    const link = this.querySelector('a');
    const heading = section?.querySelector('h2[id]');
    if (link && heading) link.href = `#${heading.id}`;
    if (this.dataset.eager === 'true' || !('IntersectionObserver' in window)) this.#load();
    else {
      this.#observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#load();
      }, { rootMargin: '150px' });
      this.#observer.observe(this.closest('.lvh-card, .lv-similar__card') || this.parentElement || this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#request += 1;
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.#controller && this.isConnected) this.#load();
  }

  async #load() {
    const request = ++this.#request;
    const signal = this.#controller.signal;
    const productId = this.dataset.productId;
    this.dataset.ratingState = 'loading';
    try {
      const result = await getProductReviews(productId, 1, { signal });
      if (signal.aborted || !this.isConnected || request !== this.#request || productId !== this.dataset.productId) return;
      const score = result.total ? formatReviewScore(result.averageRating, document.documentElement.lang || 'de') : '—';
      const scoreNode = this.querySelector('[data-rating-score]');
      const countNode = this.querySelector('[data-rating-count]');
      const fillNode = this.querySelector('.lv-rating-stars__fill');
      const labelNode = this.querySelector('[data-rating-label]');
      if (scoreNode) { scoreNode.textContent = score; scoreNode.hidden = result.total === 0; }
      if (countNode) countNode.textContent = `(${result.total})`;
      if (fillNode) fillNode.style.width = `${result.total ? result.averageRating * 20 : 0}%`;
      if (labelNode) labelNode.setAttribute('aria-label', result.total ? reviewLabel(this.dataset.ratingTemplate, { score, count: result.total }) : this.dataset.ratingEmpty);
      this.dataset.ratingState = 'loaded';
      this.hidden = false;
    } catch {
      if (signal.aborted || !this.isConnected || request !== this.#request || productId !== this.dataset.productId) return;
      this.dataset.ratingState = 'unavailable';
      this.hidden = true;
    }
  }
}
if (!customElements.get('levayo-rating')) customElements.define('levayo-rating', LevayoRating);
